import React from 'react';

interface ToggleProps {
  value: string;
  onValueChange: (value: string) => void;
  options: Record<string, React.ReactNode>;
  ariaLabel?: string;
  disabled?: boolean;
  className?: string;
}

export function Toggle({
  value,
  onValueChange,
  options,
  ariaLabel,
  disabled = false,
  className = '',
}: ToggleProps) {
  const optionKeys = Object.keys(options);
  if (optionKeys.length !== 2) {
    throw new Error('Toggle component requires exactly 2 options');
  }

  const [leftKey, rightKey] = optionKeys;
  const checked = value === rightKey;

  const handleToggle = () => {
    if (!disabled) {
      const newValue = checked ? leftKey : rightKey;
      onValueChange(newValue);
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <span className={`text-sm font-medium ${!checked ? 'text-gray-700' : 'text-gray-400'}`}>
        {options[leftKey]}
      </span>
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`
          relative inline-flex h-8 w-16 items-center rounded-full transition-colors
          ${checked ? 'bg-primary' : 'bg-gray-300'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        aria-label={ariaLabel}
        aria-checked={checked}
        role="switch"
      >
        <span
          className={`
            inline-block h-6 w-6 transform rounded-full bg-white transition-transform
            ${checked ? 'translate-x-9' : 'translate-x-1'}
          `}
        />
      </button>
      <div className="flex items-center gap-1">
        <span className={`text-sm font-medium ${checked ? 'text-gray-700' : 'text-gray-400'}`}>
          {options[rightKey]}
        </span>
      </div>
    </div>
  );
}
